import { getLocalToday } from "utils/Common/Date";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import "./LienWaiver.scss";
import Input from "../../Components/Common/Input/Input";
import DateSelect from "../../Components/Common/DateSelect/DateSelect";
import Button from "../../Components/Common/Button/Button";
import CscSubLogo from "Assets/Img/CscSubLogo";
import SignaturePad, {
  SignaturePadHandle,
} from "Components/Common/SignaturePad/SignaturePad";
import { uploadFileToS3 } from "Pages/GcPrecon/GcPreconCommercial/UploadFileToS3";
import { useToast } from "Components/Common/Toast/ToastContext";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import { CHAR_LIMIT } from "utils/Common/Constant";
import CustomModal from "Components/Common/CustomModal/CustomModal";
import { getProject } from "../../services/projectService";
import { getDraw } from "../../services/drawService";

const BASE_URL = process.env.REACT_APP_API_BASE_URL ?? "";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Currency {
  code: string;
  symbol: string;
  name: string;
}

interface MoneyAmount {
  amount: number;
  currency: Currency;
}

interface LienWaiverStakeholderPerson {
  companyName?: string;
  firstName?: string;
  lastName?: string;
}

interface LienWaiverLenderBranch {
  branchName?: string;
  companyName?: string;
}

interface LienWaiverStakeholders {
  owner?: LienWaiverStakeholderPerson;
  gc?: LienWaiverStakeholderPerson;
  lenderBranch?: LienWaiverLenderBranch;
}

interface LienWaiverProject {
  name: string;
  address: string;
  stakeholders?: LienWaiverStakeholders;
}

interface LienWaiverDrawRequest {
  Project?: LienWaiverProject;
  gcName?: string;
  drawPeriod?: string;
  ownerName?: string;
  lenderName?: string;
  drawNumber?: number;
  toDate?: string;
}

interface LienWaiverSubContractor {
  companyName?: string;
  signerName?: string;
  title?: string;
}

interface LienWaiverData {
  drawRequest?: LienWaiverDrawRequest;
  subContractor?: LienWaiverSubContractor;
  companyName?: string;
  signerName?: string;
  title?: string;
  totalAmount?: MoneyAmount;
  paidAmount?: MoneyAmount;
  remainingAmount?: MoneyAmount;
  retainageAmount?: MoneyAmount;
  isFinalPayment?: boolean;
}

interface LienWaiverApiResponse {
  data: LienWaiverData;
}

interface SubmitMoneyAmount {
  amount: number;
  currencyCode: string;
}

interface SubmitPayload {
  companyName: string;
  signerName: string;
  title: string;
  totalAmount: SubmitMoneyAmount;
  paidAmount: SubmitMoneyAmount;
  remainingAmount: SubmitMoneyAmount;
  isFinalPayment: boolean;
  signUrl: { key: string };
  signedAt: string;
  witnessName?: string;
  witnessDate?: string;
}

type PageStatus = "loading" | "ready" | "error";

// ─── Utilities ────────────────────────────────────────────────────────────────

const formatMoney = (val: MoneyAmount): string =>
  `${val.currency?.symbol ?? "$"}${Number(val.amount).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// const generateReference = (): string =>
//   `CSC-${Date.now().toString(36).toUpperCase()}`;

const DEFAULT_MONEY: MoneyAmount = {
  amount: 0,
  currency: { code: "USD", symbol: "$", name: "US Dollar" },
};

// ─── API ──────────────────────────────────────────────────────────────────────

const getLienWaiver = (token: string) =>
  axios.get<LienWaiverApiResponse>(`${BASE_URL}/lien-waivers`, {
    headers: { Authorization: `Bearer ${token}` },
  });

const submitLienWaiver = (token: string, payload: SubmitPayload) =>
  axios.post(`${BASE_URL}/lien-waivers`, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });

// ─── Stakeholder name resolver ────────────────────────────────────────────────

const resolvePersonName = (person?: LienWaiverStakeholderPerson): string => {
  const full = [person?.firstName, person?.lastName].filter(Boolean).join(" ");
  return full || person?.companyName || "";
};

// ─── Skeleton loader ──────────────────────────────────────────────────────────

function LienWaiverSkeleton() {
  return (
    <div className="wrap">
      {/* Notice */}
      <Skeleton
        variant="card"
        width="100%"
        height={72}
        style={{ marginBottom: 16 }}
      />

      {/* Project Information */}
      <div className="card">
        <Skeleton width="45%" height={13} style={{ marginBottom: 18 }} />
        <div className="infoGrid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="infoItem">
              <Skeleton width="55%" height={10} style={{ marginBottom: 6 }} />
              <Skeleton width="80%" height={14} />
            </div>
          ))}
        </div>
      </div>

      {/* Your Information */}
      <div className="card">
        <Skeleton width="50%" height={13} style={{ marginBottom: 18 }} />
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="field">
            <Skeleton width="35%" height={10} style={{ marginBottom: 6 }} />
            <Skeleton width="100%" height={46} borderRadius={8} />
          </div>
        ))}
      </div>

      {/* Amounts */}
      <div className="card">
        <Skeleton width="55%" height={13} style={{ marginBottom: 18 }} />
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="card"
            width="100%"
            height={44}
            style={{ marginBottom: 8 }}
          />
        ))}
      </div>

      {/* Submit */}
      <Skeleton variant="card" width="100%" height={54} />
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function LienWaiver() {
  const {
    token = "",
    projectId = "",
    drawId = "",
    lienWaiverId = "",
  } = useParams<{
    token?: string;
    projectId?: string;
    drawId?: string;
    lienWaiverId?: string;
  }>();
  const isPreview = Boolean(projectId);
  const showToast = useToast();
  const padRef = useRef<SignaturePadHandle>(null);
  const subNameRef = useRef<HTMLDivElement>(null);
  const signerNameRef = useRef<HTMLDivElement>(null);
  const signatureRef = useRef<HTMLDivElement>(null);
  const [pageStatus, setPageStatus] = useState<PageStatus>("loading");
  // ── Project info prefilled from API ──
  const [projectName, setProjectName] = useState("—");
  const [projectAddress, setProjectAddress] = useState("—");
  const [ownerName, setOwnerName] = useState("—");
  const [gcName, setGcName] = useState("—");
  const [drawPeriod, setDrawPeriod] = useState("—");
  const [lenderName, setLenderName] = useState("—");
  // ── Editable form fields ──
  const [subName, setSubName] = useState("");
  const [signerName, setSignerName] = useState("");
  const [signerTitle, setSignerTitle] = useState("");
  const [waiveDate, setWaiveDate] = useState(getLocalToday);
  const [witnessName, setWitnessName] = useState("");
  const [witnessDate, setWitnessDate] = useState(getLocalToday);
  const [isFinalPayment, setIsFinalPayment] = useState(false);
  // ── Amount state (from API) ──
  const [totalAmount, setTotalAmount] = useState<MoneyAmount>(DEFAULT_MONEY);
  const [remainingAmount, setRemainingAmount] =
    useState<MoneyAmount>(DEFAULT_MONEY);
  const [retainageAmount, setRetainageAmount] =
    useState<MoneyAmount>(DEFAULT_MONEY);
  // ── Signature state ──
  const [hasSignature, setHasSignature] = useState(false);
  const [sigUploading, setSigUploading] = useState(false);
  // ── Submit / success state ──
  const [submitting, setSubmitting] = useState(false);
  const [wasSubmitted, setWasSubmitted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    subName: "",
    signerName: "",
    signature: "",
  });
  // const [referenceNumber, setReferenceNumber] = useState("—");
  const [submittedAt, setSubmittedAt] = useState("");
  const [progress, setProgress] = useState(20);

  // ── Fetch waiver data on mount using token ──
  useEffect(() => {
    if (isPreview) return;
    if (!token) {
      setPageStatus("error");
      return;
    }
    getLienWaiver(token)
      .then((res) => {
        const data = res.data?.data;
        if (!data) {
          setPageStatus("error");
          return;
        }
        const dr = data.drawRequest;
        setProjectName(dr?.Project?.name ?? "—");
        setProjectAddress(dr?.Project?.address ?? "—");
        const stakeholders = dr?.Project?.stakeholders;
        setOwnerName(
          resolvePersonName(stakeholders?.owner) || dr?.ownerName || "—",
        );
        setGcName(resolvePersonName(stakeholders?.gc) || dr?.gcName || "—");
        const drawNo = dr?.drawNumber
          ? `Draw #${String(dr.drawNumber).padStart(2, "0")}`
          : "Draw #--";
        const throughDate = dr?.toDate
          ? new Date(dr.toDate).toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            })
          : "—";
        setDrawPeriod(`${drawNo} · Through ${throughDate}`);
        const lb = stakeholders?.lenderBranch;
        setLenderName(lb?.companyName || dr?.lenderName || "—");
        const sub = data.subContractor;
        setSubName(sub?.companyName ?? data.companyName ?? "");
        setSignerName(sub?.signerName ?? data.signerName ?? "");
        setSignerTitle(sub?.title ?? data.title ?? "");
        if (data.totalAmount) setTotalAmount(data.totalAmount);
        if (data.remainingAmount) setRemainingAmount(data.remainingAmount);
        if (data.retainageAmount) setRetainageAmount(data.retainageAmount);
        setIsFinalPayment(data.isFinalPayment ?? false);
        setPageStatus("ready");
      })
      .catch(() => setPageStatus("error"));
  }, [token, isPreview]);

  // ── Fetch project data for preview (preview flow) ──
  useEffect(() => {
    if (!isPreview || !projectId) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (getProject(projectId) as Promise<{ data: any; error: any }>)
      .then(({ data, error }) => {
        if (error || !data) {
          setPageStatus("error");
          return;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const p = (data?.data ?? data) as Record<string, any>;

        setProjectName((p?.projectName as string) ?? "—");
        setProjectAddress((p?.projectAddress as string) ?? "—");
        setOwnerName(resolvePersonName(p?.owner) || "—");
        setGcName(resolvePersonName(p?.gc) || "—");
        setLenderName((p?.lender?.companyName as string) ?? "—");
        setDrawPeriod("—");
        setPageStatus("ready");
      })
      .catch(() => setPageStatus("error"));
  }, [projectId, isPreview]);

  // ── Fetch draw data for preview (sub/signer/amount fields) ──
  useEffect(() => {
    if (!isPreview || !projectId || !drawId) return;
    const fetchDrawData = async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = (await getDraw(projectId, drawId)) as {
          data: any;
          error: any;
        };
        if (error || !data) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const draw = (data?.data ?? data) as Record<string, any>;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const lienWaiver = ((draw?.lienItems ?? []) as Record<string, any>[])
          .flatMap((item) => (item.lienWaivers ?? []) as Record<string, any>[])
          .find((w) => w.id === lienWaiverId);
        if (!lienWaiver) return;
        setSubName((lienWaiver.companyName as string) ?? "");
        setSignerName((lienWaiver.signerName as string) ?? "");
        setSignerTitle((lienWaiver.title as string) ?? "");
        if (lienWaiver.totalAmount)
          setTotalAmount(lienWaiver.totalAmount as MoneyAmount);
        if (lienWaiver.retainageAmount)
          setRetainageAmount(lienWaiver.retainageAmount as MoneyAmount);
        setIsFinalPayment((lienWaiver.isFinalPayment as boolean) ?? false);
      } catch {
        // non-critical — project info already shown
      }
    };
    fetchDrawData();
  }, [projectId, drawId, lienWaiverId, isPreview]);

  // ── Progress bar calculation ──
  const pageSummary = useMemo(
    () => ({
      submittedBy: subName
        ? `${subName}${signerName ? ` · ${signerName}` : ""}`
        : "—",
    }),
    [subName, signerName],
  );

  const displayPaidAmount = useMemo<MoneyAmount>(
    () => ({
      amount: totalAmount.amount - retainageAmount.amount,
      currency: retainageAmount.currency,
    }),
    [totalAmount.amount, retainageAmount],
  );

  const displayRemainingAmount = useMemo<MoneyAmount>(
    () => ({
      amount: retainageAmount.amount,
      currency: retainageAmount.currency,
    }),
    [retainageAmount],
  );

  useEffect(() => {
    let width = 20;
    if (subName.trim()) width += 20;
    if (signerName.trim()) width += 15;
    if (hasSignature) width += 25;
    setProgress(Math.min(width, 80));
  }, [subName, signerName, hasSignature]);

  // ── Submit handler ──
  const handleSubmit = async () => {
    if (isPreview) return;
    const newErrors = { subName: "", signerName: "", signature: "" };
    if (!subName.trim()) {
      newErrors.subName = "Company name is required.";
    } else if (subName.trim().length > CHAR_LIMIT.NAME) {
      newErrors.subName = `Company name must be ${CHAR_LIMIT.NAME} characters or less.`;
    }
    if (!signerName.trim()) {
      newErrors.signerName = "Authorized signer name is required.";
    } else if (signerName.trim().length > CHAR_LIMIT.NAME) {
      newErrors.signerName = `Signer name must be ${CHAR_LIMIT.NAME} characters or less.`;
    }
    if (!hasSignature) {
      newErrors.signature = "Please sign the waiver before submitting.";
    }
    if (newErrors.subName || newErrors.signerName || newErrors.signature) {
      setFieldErrors(newErrors);
      if (newErrors.subName) {
        subNameRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        subNameRef.current?.querySelector("input")?.focus();
      } else if (newErrors.signerName) {
        signerNameRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        signerNameRef.current?.querySelector("input")?.focus();
      } else {
        signatureRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
      return;
    }

    setSigUploading(true);
    let signUrl = "";
    try {
      const blob = await padRef.current!.getBlob();
      const file = new File([blob], "signature.png", { type: "image/png" });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result: any = await uploadFileToS3(file, token);
      signUrl =
        result?.key ??
        result?.Key ??
        result?.data?.key ??
        result?.data?.Key ??
        "";
    } catch {
      showToast("Signature upload failed. Please try again.");
      setSigUploading(false);
      return;
    } finally {
      setSigUploading(false);
    }

    const toSubmitMoney = (m: MoneyAmount): SubmitMoneyAmount => ({
      amount: m.amount,
      currencyCode: m.currency?.code ?? "USD",
    });

    const payload: SubmitPayload = {
      companyName: subName.trim(),
      signerName: signerName.trim(),
      title: signerTitle.trim(),
      totalAmount: toSubmitMoney(totalAmount),
      paidAmount: toSubmitMoney(displayPaidAmount),
      remainingAmount: toSubmitMoney(remainingAmount),
      isFinalPayment,
      signUrl: { key: signUrl },
      signedAt: new Date().toISOString(),
      ...(witnessName.trim() ? { witnessName: witnessName.trim() } : {}),
      ...(witnessDate ? { witnessDate } : {}),
    };

    setSubmitting(true);
    try {
      await submitLienWaiver(token, payload);
      // setReferenceNumber(generateReference());
      setWasSubmitted(true);
      setSubmittedAt(
        new Date().toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        }),
      );
      setProgress(100);
      showToast("Lien waiver submitted successfully.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      showToast("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Shared header ──
  const Header = (
    <>
      <div className="header">
        <div className="headerLogo">
          <CscSubLogo />
        </div>
        <div className="headerText">
          <div className="header-title">Mechanic's Lien Waiver</div>
          <div className="header-sub">
            Consultant Services New England · Secure signing link
          </div>
        </div>
      </div>
      <div className="progress-bar">
        <div
          className="progress-fill"
          style={{ width: `${wasSubmitted ? 100 : progress}%` }}
        />
      </div>
    </>
  );

  // ── Main render ──
  return (
    <div className="lienWaiver">
      <CustomModal
        show={pageStatus === "error"}
        // title="Invalid or Expired Link"
        isCrossBtn={false}
        isFooterVisible={false}
        backdrop="static"
        keyboard={false}
        isHeaderVisible={true}
      >
        <div className="lwBlockIcon">🔒</div>
        <div className="lwBlockTitle">Invalid or Expired Link</div>
        <div className="lwBlockMsg">
          This signing link is invalid or expired. Please contact CSC New
          England or your General Contractor.
        </div>
      </CustomModal>

      {Header}

      {pageStatus === "loading" ? (
        <LienWaiverSkeleton />
      ) : (
        <div className="wrap">
          {!wasSubmitted ? (
            <>
              {isPreview ? (
                <div className="notice notice--preview">
                  <div className="noticeIcon">👁️</div>
                  <div className="noticeText">
                    <strong>Preview Mode.</strong> This is a read-only preview
                    of the lien waiver. No submission is allowed.
                  </div>
                </div>
              ) : (
                <div className="notice">
                  <div className="noticeIcon">🔒</div>
                  <div className="noticeText">
                    <strong>No account needed.</strong>
                    This is a secure, one-time signing link sent to you by your
                    general contractor. Review the pre-filled information below,
                    complete any blank fields, and sign at the bottom.
                  </div>
                </div>
              )}

              <div className="card">
                <div className="cardTitle">Project Information</div>
                <div className="infoGrid">
                  <div className="infoItem">
                    <div className="infoLabel">Project Name</div>
                    <div className="infoVal">{projectName}</div>
                  </div>
                  <div className="infoItem">
                    <div className="infoLabel">Project Address</div>
                    <div className="infoVal">{projectAddress}</div>
                  </div>
                  <div className="infoItem">
                    <div className="infoLabel">Property Owner</div>
                    <div className="infoVal">{ownerName}</div>
                  </div>
                  <div className="infoItem">
                    <div className="infoLabel">General Contractor</div>
                    <div className="infoVal">{gcName}</div>
                  </div>
                  <div className="infoItem">
                    <div className="infoLabel">Draw / Payment Period</div>
                    <div className="infoVal">{drawPeriod}</div>
                  </div>
                  <div className="infoItem">
                    <div className="infoLabel">Lender</div>
                    <div className="infoVal">{lenderName}</div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="cardTitle">
                  Your Information — Service Provider
                </div>
                <div className="field" ref={subNameRef}>
                  <label htmlFor="subName">
                    Your Company Name <span className="req">*</span>
                  </label>
                  <Input
                    id="subName"
                    type="text"
                    value={subName}
                    disabled={isPreview}
                    maxLength={CHAR_LIMIT.NAME}
                    className={fieldErrors.subName ? "error-input-red" : ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setSubName(e.target.value);
                      if (fieldErrors.subName)
                        setFieldErrors((prev) => ({ ...prev, subName: "" }));
                    }}
                    placeholder="Enter your company name"
                  />
                  {fieldErrors.subName && (
                    <p className="field-error">{fieldErrors.subName}</p>
                  )}
                </div>
                <div className="field" ref={signerNameRef}>
                  <label htmlFor="signerName">
                    Authorized Signer Name <span className="req">*</span>
                  </label>
                  <Input
                    id="signerName"
                    type="text"
                    value={signerName}
                    disabled={isPreview}
                    maxLength={CHAR_LIMIT.NAME}
                    className={fieldErrors.signerName ? "error-input-red" : ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setSignerName(e.target.value);
                      if (fieldErrors.signerName)
                        setFieldErrors((prev) => ({ ...prev, signerName: "" }));
                    }}
                    placeholder="Full legal name"
                  />
                  {fieldErrors.signerName && (
                    <p className="field-error">{fieldErrors.signerName}</p>
                  )}
                </div>
                <div className="field">
                  <label htmlFor="signerTitle">Title</label>
                  <Input
                    id="signerTitle"
                    type="text"
                    value={signerTitle}
                    disabled={isPreview}
                    maxLength={CHAR_LIMIT.SHORT}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setSignerTitle(e.target.value)
                    }
                    placeholder="e.g. Owner, President, Office Manager"
                  />
                </div>
              </div>

              <div className="card">
                <div className="cardTitle">Waiver of Lien — Unconditional</div>
                <div className="legal">
                  The undersigned Service Provider is or will be engaged in
                  providing labor, services and/or materials related to a
                  construction project at <strong>{projectAddress}</strong>{" "}
                  (hereafter, "Construction Services").
                  <br />
                  <br />
                  In consideration of{" "}
                  <span className="fill-field highlight-amount">
                    {formatMoney(totalAmount)}
                  </span>
                  , receipt of which is hereby acknowledged, the Service
                  Provider hereby waives and releases any and all liens or
                  claims of, or right to, liens arising out of Construction
                  Services performed on or before <strong>{drawPeriod}</strong>{" "}
                  (the "Waiver Date").
                  <br />
                  <br />
                  The terms of any agreement related to Construction Services
                  are as follows:
                </div>

                <div className="amounts-grid">
                  <div className="amount-row highlight">
                    <div className="amount-label">
                      1. Contract Amount or Total Invoices
                    </div>
                    <div className="amount-val">{formatMoney(totalAmount)}</div>
                  </div>
                  <div className="amount-row">
                    <div className="amount-label">2. Total Paid</div>
                    <div className="amount-val">
                      {formatMoney(displayPaidAmount)}
                    </div>
                  </div>
                  <div className="amount-row">
                    <div className="amount-label">3. Balance Remaining</div>
                    <div className="amount-val">
                      {formatMoney(displayRemainingAmount)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="cardTitle">
                  Final Payment Certification (Complete only if this is FINAL
                  payment)
                </div>
                <div className="legal">
                  The Service Provider hereby waives and releases any right
                  which it now has or in the future may have to claim a
                  mechanic's lien or any other lien rights, and waives and
                  releases all other claims of any kind associated with
                  Construction Services. The Service Provider also certifies
                  that it has made (or will make) payment to all of its
                  subcontractors, suppliers, and employees for all items
                  connected with the Construction Services.
                </div>
                <div className="checkbox-row">
                  <label className="checkbox-control">
                    <Input
                      type="checkbox"
                      checked={isFinalPayment}
                      disabled={isPreview}
                      onChange={() => setIsFinalPayment(true)}
                    />
                    Yes — this is final payment
                  </label>
                  <label className="checkbox-control">
                    <Input
                      type="checkbox"
                      checked={!isFinalPayment}
                      disabled={isPreview}
                      onChange={() => setIsFinalPayment(false)}
                    />
                    No — partial payment
                  </label>
                </div>
              </div>

              <div className="card">
                <div className="cardTitle">Waiver Date</div>
                <div className="field">
                  <label>
                    Date Signed <span className="req">*</span>
                  </label>
                  <DateSelect
                    id="waiveDate"
                    value={waiveDate}
                    min={getLocalToday()}
                    disabled={isPreview}
                    onChange={setWaiveDate}
                  />
                </div>
              </div>
              {!isPreview && (
                <div className="card" ref={signatureRef}>
                  <div className="cardTitle">Authorized Signature</div>
                  <p className="sigDesc">
                    The Service Provider understands and is voluntarily
                    executing this Mechanic's Lien Waiver. Sign below using your
                    finger, stylus, or mouse.
                  </p>
                  <div className="sig-section">
                    <div className="sig-label">Draw your signature below *</div>
                    <SignaturePad
                      ref={padRef}
                      onSignedChange={(signed) => {
                        setHasSignature(signed);
                        if (signed && fieldErrors.signature)
                          setFieldErrors((prev) => ({
                            ...prev,
                            signature: "",
                          }));
                      }}
                      statusId="lw-sig-status"
                    />
                    {fieldErrors.signature && (
                      <p className="field-error">{fieldErrors.signature}</p>
                    )}
                  </div>
                </div>
              )}

              <div className="card">
                <div className="cardTitle">Witness (Optional)</div>
                <div className="field-row">
                  <div className="field">
                    <label htmlFor="witnessName">Witness Name</label>
                    <Input
                      id="witnessName"
                      type="text"
                      value={witnessName}
                      disabled={isPreview}
                      maxLength={CHAR_LIMIT.NAME}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setWitnessName(e.target.value)
                      }
                      placeholder="Witness full name"
                    />
                  </div>
                  <div className="field">
                    <label>Witness Date</label>
                    <DateSelect
                      id="witnessDate"
                      value={witnessDate}
                      min={getLocalToday()}
                      disabled={isPreview}
                      onChange={setWitnessDate}
                    />
                  </div>
                </div>
              </div>

              <div className="disclaimer">
                Consultant Services New England LLC is NOT responsible for
                signature verification on Waivers of Lien. Item # is taken from
                Column A of the AIA.
              </div>
              {!isPreview && (
                <Button
                  className="submit-btn"
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting || sigUploading}
                >
                  {!submitting && !sigUploading && (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4 10l4 4 8-8"
                        stroke="#fff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                  {sigUploading
                    ? "Uploading signature…"
                    : submitting
                      ? "Submitting…"
                      : "Submit Signed Waiver"}
                </Button>
              )}
            </>
          ) : (
            <div id="successScreen">
              <div className="card">
                <div className="success">
                  <div className="successCircle">✅</div>
                  <div className="successTitle">Waiver Submitted</div>
                  <div className="successSub">
                    Your lien waiver has been submitted successfully and
                    delivered to Consultant Services New England and {gcName}.
                    No further action is required.
                  </div>
                  {/* <div className="refBox">
                    <div className="refLabel">Confirmation Reference</div>
                    <div className="refVal">{referenceNumber}</div>
                  </div> */}
                  <div className="summaryCard">
                    <div className="summaryTitle">Summary</div>
                    <div className="summaryRow">
                      <div>
                        Project: <strong>{projectName}</strong>
                      </div>
                      <div>
                        Submitted by: <strong>{pageSummary.submittedBy}</strong>
                      </div>
                      <div>
                        Amount: <strong>{formatMoney(totalAmount)}</strong>
                      </div>
                      <div>
                        Date: <strong>{submittedAt}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="summaryNote">
                    You may close this window. A copy of this waiver has been
                    recorded in the CSC New England platform.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
